/**
 * `/reports/weekly/:division` — the weekly status report for one division.
 * Port of the demo's `buildWeekly(kind)` (demo line 2601) rendered in its
 * report pane (`renderReports`, demo 2652).
 *
 * `:division` is the report kind — `drills`, `loadhaul` or `surface` — so the
 * address matches the assistant's `generate_report` kinds one for one.
 *
 * Five sections under the OEM letterhead, in the demo's order: summary by
 * mine, machines needing attention, the largest work orders, preventive
 * maintenance due in the next 14 days, and energy. Every availability,
 * breakdown and cost figure, and every work-order number, opens its rows.
 *
 * @see src/versions/v1/modules/reports/WeeklyStatusScene.transform.ts
 * @see docs/logic/reports/weekly.md
 */

import { useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  HelpAnchor,
  InlineError,
  SkeletonRows,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { Commentable } from '@/shared/comments/Commentable';
import { AskStrip } from '../../chrome-oem/AskStrip';
import { FLEET, HEALTH } from '../../data/core';
import { CONDITION_BAND_LABEL } from '../../schema/machine';
import type {
  ReportRef,
  ReportSource,
  ReportWeekPack,
  WeeklyReportKind,
  WeeklyReportPack,
} from '../../schema/report';
import { unpackTable } from '../../shared/columnar';
import { DocFoot } from '../../shared/docs/DocFoot';
import { Letterhead } from '../../shared/docs/Letterhead';
import { fmt$, groupNum } from '../../shared/oem-engine';
import { BandPill, SourceLink } from '../../shared/oem-tiles';
import { useOemPersona } from '../../shared/OemPersonaContext';
import { isWeeklyKind } from '../../shared/report-catalog';
import { useOemData, type ShiftDailyRow, type WorkOrder } from '../../shared/useOemData';
import type { SourceCalc } from '../../chrome-oem/SourceDrawerBody';
import { useRowsDrawer } from '../service/source-slice';
import { NotFoundScene } from '../NotFoundScene';
import { ReportSidePanel, ReportToolbar } from './ReportActions';
import { availabilityCalc, costCalc } from './report-calc';
import { MachineLink, Para, ReportLayout, ReportPaper, SectionHeading } from './ReportDoc';
import {
  divisionMachineIds,
  divisionTotalLine,
  weeklyReport,
  weekShiftRows,
  weekWorkOrders,
} from './WeeklyStatusScene.transform';

export function WeeklyStatusScene() {
  const { division } = useParams<{ division: string }>();
  if (!isWeeklyKind(division)) return <NotFoundScene />;
  return <WeeklyStatusReportView kind={division} />;
}

function WeeklyStatusReportView({ kind }: { kind: WeeklyReportKind }) {
  const reports = useOemData('reports');
  const telemetry = useOemData('telemetry');
  const workOrdersData = useOemData('work-orders');
  const { persona } = useOemPersona();
  const openRows = useRowsDrawer();
  const paperRef = useRef<HTMLDivElement>(null);
  const address: ReportRef = { kind, mine: null };

  const shiftDaily = useMemo(
    () => (telemetry.data ? unpackTable<ShiftDailyRow>(telemetry.data.shift_daily) : null),
    [telemetry.data],
  );
  const workOrders = useMemo(
    () => (workOrdersData.data ? unpackTable<WorkOrder>(workOrdersData.data.wos) : null),
    [workOrdersData.data],
  );

  const report = useMemo(() => {
    if (!reports.data || !shiftDaily || !workOrders) return null;
    const pack = reports.data.reports[kind] as unknown as WeeklyReportPack | undefined;
    const week = reports.data.reports.week as unknown as ReportWeekPack | undefined;
    if (!pack || !week) return null;
    return weeklyReport(kind, { pack, week, shiftDaily, workOrders });
  }, [kind, reports.data, shiftDaily, workOrders]);

  const ids = useMemo(() => (report ? divisionMachineIds(report.division) : []), [report]);
  const error = reports.error ?? telemetry.error ?? workOrdersData.error;

  const openShift = useCallback(
    (machineIds: readonly string[], title: string, shown?: number) => {
      if (!shiftDaily) return;
      const rows = weekShiftRows(machineIds, shiftDaily);
      openRows('shift', rows, {
        title,
        filter: `${machineIds.length} machines, week to 06 Sep`,
        total: shiftDaily.length,
        // Demo 2603 — the availability cell's calc note.
        ...(shown != null ? { calc: availabilityCalc(rows, shown) } : {}),
      });
    },
    [openRows, shiftDaily],
  );

  const openWos = useCallback(
    (rows: readonly WorkOrder[], title: string, filter: string, calc?: SourceCalc) =>
      openRows('wos', rows, { title, filter, total: workOrders?.length, ...(calc ? { calc } : {}) }),
    [openRows, workOrders],
  );

  const openWo = (woId: string) =>
    openWos(
      (workOrders ?? []).filter((w) => w.wo_id === woId),
      `Work order ${woId}`,
      `wo_id=${woId}`,
    );

  const onSource = (s: ReportSource) => {
    if (s.dataset === 'shift') return () => openShift(ids, 'Shift records behind the report');
    if (s.dataset === 'wos' && workOrders) {
      return () => openWos(weekWorkOrders(ids, workOrders), 'Work orders this week', 'division, week to 06 Sep');
    }
    if (s.dataset === 'health') {
      return () =>
        openRows('health', HEALTH.filter((h) => ids.includes(h.machine_id)), {
          title: 'Condition register',
          filter: `division=${report?.division ?? ''}`,
        });
    }
    return null;
  };

  const mineIds = (mine: string) =>
    FLEET.filter((f) => f.mine === mine && ids.includes(f.machine_id)).map((f) => f.machine_id);

  return (
    <SurfacePageLayout width="full">
      <Commentable name="WeeklyStatusScene">
        <div className="contents">
          <SurfacePageBody className="@container space-y-5" as="div">
            <SurfacePageHeader
              kicker="Reports"
              title={report?.title ?? 'Weekly status'}
              description={
                report
                  ? `${report.sub} · generated ${report.generated} by the assistant for ${persona.name}`
                  : 'Building the report from the live workspace data…'
              }
              actions={<HelpAnchor doc="reports/weekly" />}
            />

            <ReportToolbar report={report} address={address} docRef={paperRef} />
            <AskStrip view="reports" />

            {error ? (
              <InlineError>{`The report did not build: ${error.message}`}</InlineError>
            ) : !report ? (
              <SkeletonRows count={10} />
            ) : (
              <ReportLayout
                doc={
                  <Commentable name="WeeklyDocument">
                    <ReportPaper paperRef={paperRef} label={report.title}>
                      <Letterhead
                        product
                        docType="Weekly status report"
                        docId={report.docId}
                        rev="A"
                        date="09 Sep 2026"
                        classification="Internal — customer shareable"
                      />
                      <h2 className="text-[18px] font-semibold" style={{ color: 'var(--ink-primary)' }}>
                        {report.division} — weekly status
                      </h2>
                      <p className="note mb-2 text-[11.5px]" style={{ color: 'var(--ink-tertiary)' }}>
                        Week {report.weekLabel} · generated {report.generated} from SAP PM, Pitram /
                        live telematics, Salesforce and the cost ledger. Every figure reconciles to
                        the workspace.
                      </p>

                      <SectionHeading>1. Summary</SectionHeading>
                      <Table className="min-w-[720px]">
                        <THead>
                          <TR>
                            <TH>Mine</TH>
                            <TH className="num text-right">Machines</TH>
                            <TH className="num text-right">Availability %</TH>
                            <TH className="num text-right">Breakdowns</TH>
                            <TH className="num text-right">Downtime h</TH>
                            <TH className="num text-right">Cost this week A$</TH>
                            <TH className="num text-right">CO₂-e t</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {report.rows.map((r) => {
                            const wos = weekWorkOrders(mineIds(r.mine), workOrders ?? []);
                            return (
                              <TR key={r.mine}>
                                <TD>{r.name}</TD>
                                <TD className="num mono text-right tabular-nums">{r.machines}</TD>
                                <TD className="num text-right">
                                  <SourceLink
                                    onClick={() =>
                                      openShift(
                                        mineIds(r.mine),
                                        `Shift records · ${r.name} · ${report.division}`,
                                        r.availability,
                                      )
                                    }
                                  >
                                    {r.availability}
                                  </SourceLink>
                                </TD>
                                <TD className="num text-right">
                                  <SourceLink
                                    onClick={() =>
                                      openWos(
                                        wos.filter((w) => w.wo_type === 'Breakdown'),
                                        `Breakdowns · ${r.name}`,
                                        `mine=${r.mine}, wo_type=Breakdown, week to 06 Sep`,
                                      )
                                    }
                                  >
                                    {r.breakdowns}
                                  </SourceLink>
                                </TD>
                                <TD className="num mono text-right tabular-nums">{r.downtime_h}</TD>
                                <TD className="num text-right">
                                  <SourceLink
                                    onClick={() =>
                                      openWos(
                                        wos,
                                        `Work orders this week · ${r.name}`,
                                        `mine=${r.mine}, week to 06 Sep`,
                                        costCalc(wos, r.cost),
                                      )
                                    }
                                  >
                                    {groupNum(r.cost)}
                                  </SourceLink>
                                </TD>
                                <TD className="num mono text-right tabular-nums">{r.co2_t}</TD>
                              </TR>
                            );
                          })}
                        </TBody>
                      </Table>
                      <Para>{divisionTotalLine(report, fmt$)}</Para>

                      <SectionHeading>2. Machines needing attention</SectionHeading>
                      <Table className="min-w-[860px]">
                        <THead>
                          <TR>
                            <TH>Machine</TH>
                            <TH>Mine</TH>
                            <TH>Model</TH>
                            <TH>Condition</TH>
                            <TH>Component</TH>
                            <TH className="num text-right">Score /100</TH>
                            <TH>Note</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {report.attention.map((x) => (
                            <TR key={x.machine_id}>
                              <TD className="whitespace-nowrap">
                                <MachineLink id={x.machine_id} />
                              </TD>
                              <TD>{x.mine}</TD>
                              <TD>{x.model}</TD>
                              <TD>
                                <BandPill band={x.band}>{CONDITION_BAND_LABEL[x.band]}</BandPill>
                              </TD>
                              <TD className="text-[11.5px]">{x.component}</TD>
                              <TD className="num mono text-right tabular-nums">{x.score}</TD>
                              <TD className="text-[11.5px]">{x.note}</TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                      <Para note>
                        Machine condition is its lowest-scoring component. Fair band this week
                        (monitor only):{' '}
                        {report.fair.length
                          ? report.fair.map((r, i) => (
                              <span key={r.machine_id}>
                                {i ? ', ' : ''}
                                <MachineLink id={r.machine_id} /> {r.component} {r.score}
                              </span>
                            ))
                          : 'none'}
                        .
                      </Para>

                      <SectionHeading>3. Largest work orders this week</SectionHeading>
                      {report.topWorkOrders.length ? (
                        <Table className="min-w-[760px]">
                          <THead>
                            <TR>
                              <TH>WO</TH>
                              <TH>Machine</TH>
                              <TH>Type</TH>
                              <TH>Component</TH>
                              <TH className="num text-right">Down h</TH>
                              <TH className="num text-right">All-in A$</TH>
                              <TH>Crew</TH>
                            </TR>
                          </THead>
                          <TBody>
                            {report.topWorkOrders.map((w) => (
                              <TR key={w.wo_id}>
                                <TD className="whitespace-nowrap">
                                  <SourceLink onClick={() => openWo(w.wo_id)} className="mono">
                                    {w.wo_id}
                                  </SourceLink>
                                </TD>
                                <TD>
                                  <MachineLink id={w.machine_id} />
                                </TD>
                                <TD>{w.wo_type}</TD>
                                <TD className="text-[11.5px]">{w.component}</TD>
                                <TD className="num mono text-right tabular-nums">{w.downtime_h}</TD>
                                <TD className="num mono text-right tabular-nums">{groupNum(Math.round(w.allin))}</TD>
                                <TD className="text-[11.5px]">{w.crew}</TD>
                              </TR>
                            ))}
                          </TBody>
                        </Table>
                      ) : (
                        <Para>No reactive work orders this week.</Para>
                      )}

                      <SectionHeading>4. Preventive maintenance due in the next 14 days</SectionHeading>
                      <Table className="min-w-[560px]">
                        <THead>
                          <TR>
                            <TH>WO</TH>
                            <TH>Machine</TH>
                            <TH>Mine</TH>
                            <TH>PM</TH>
                            <TH>Scheduled</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {report.pmDue.map((p) => (
                            <TR key={p.wo_id}>
                              <TD className="whitespace-nowrap">
                                <SourceLink onClick={() => openWo(p.wo_id)} className="mono">
                                  {p.wo_id}
                                </SourceLink>
                              </TD>
                              <TD>
                                <MachineLink id={p.machine_id} />
                              </TD>
                              <TD>{p.mine}</TD>
                              <TD>{p.pm_code}</TD>
                              <TD className="mono">{p.created.slice(0, 10)}</TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>

                      <SectionHeading>5. Energy</SectionHeading>
                      <Table className="min-w-[520px]">
                        <THead>
                          <TR>
                            <TH>Mine</TH>
                            <TH className="num text-right">Diesel L</TH>
                            <TH className="num text-right">Electricity kWh</TH>
                            <TH className="num text-right">CO₂-e t</TH>
                          </TR>
                        </THead>
                        <TBody>
                          {report.rows.map((r) => (
                            <TR key={r.mine}>
                              <TD>{r.name}</TD>
                              <TD className="num mono text-right tabular-nums">{groupNum(r.diesel_l)}</TD>
                              <TD className="num mono text-right tabular-nums">{groupNum(r.kwh)}</TD>
                              <TD className="num mono text-right tabular-nums">{r.co2_t}</TD>
                            </TR>
                          ))}
                        </TBody>
                      </Table>
                      <Para note>
                        Scope-1 diesel at 2.68 kg CO₂-e/L; scope-2 electricity at each site&apos;s
                        grid factor. Battery-electric loaders and electric drilling appear under kWh.
                      </Para>

                      <DocFoot docId={`${report.docId} Rev A`} controlled={false} />
                    </ReportPaper>
                  </Commentable>
                }
                side={
                  <Commentable name="WeeklySources">
                    <div className="min-w-0">
                      <ReportSidePanel report={report} address={address} onSource={onSource} />
                    </div>
                  </Commentable>
                }
              />
            )}
          </SurfacePageBody>
        </div>
      </Commentable>
    </SurfacePageLayout>
  );
}
